import {useForceUpdate, useInstanceValue, useRenderState} from '../';
import {
    arrayEqual,
    ArrayOrSingle,
    asArray,
    Dictionary,
    Evaluable,
    evaluateWhenFunction,
    EventEmitter,
    identity,
    immerProduce,
    IObservable,
    mapPushToMultiValuesArray,
    objectFilterEntries,
    objectGetKeys,
    objectMapValues,
    objectShallowEqual,
    Promisable
} from '@wix/devzai-utils-common';
import {FormEvent} from 'react';

export namespace FormController {

    export type FormDataValidationFunction<T> =
        (formData: T, errorsReporter: IFormErrorsReporter<T>) => void;

    export type FormDataSanitizationFunction<T> = (formData: T) => T;

    export type DisabledFieldsSpec<T> = Evaluable<(formData: T) => {[K in keyof T]?: boolean}>

    export type FormFieldsAutoFill<T> = {
        [FIELD_NAME in keyof T]?: (formData: T) => T[FIELD_NAME];
    };

    export type FormFieldValidator<V> = (value: V) => ArrayOrSingle<string> | undefined

    export interface Options<T extends {}> {
        sanitizationFunction?: FormDataSanitizationFunction<T>;
        validationFunction?: FormDataValidationFunction<T>;
        formFieldsAutoFill?: FormFieldsAutoFill<T>;
        onValueChange?: {
            [fieldName in keyof T]?: (value: T[fieldName]) => void;
        };
        onUpdate?: (formController: FormController<T>) => void;

        disabledFields?: DisabledFieldsSpec<T>;
    }
}

export interface IFormErrorsReporter<T> {

    report<K extends keyof T & string>(fieldName: K, evaluableError: Evaluable<FormController.FormFieldValidator<T[K]>>) : void;

    hasErrors () : boolean;
}

class FormErrorsReporter<T> implements IFormErrorsReporter<T> {

    private reportedErrors = new Map<keyof T & string, string[]>();
    private formData;
    private disabledFields;

    constructor (formData: T, disabledFields: {[key in keyof T]: boolean}) {
        this.formData = formData;
        this.disabledFields = disabledFields;
    }

    report<K extends keyof T & string>(fieldName: K, evaluableError: Evaluable<FormController.FormFieldValidator<T[K]>>) {

        if (this.disabledFields[fieldName]) {
            return;
        }

        const evaluatedErrors = evaluateWhenFunction(evaluableError, this.formData[fieldName]);

        if (evaluatedErrors) {
            const evaluatedErrorsArr = asArray(evaluatedErrors);

            for (const error of evaluatedErrorsArr) {
                mapPushToMultiValuesArray(this.reportedErrors, fieldName, error);
            }
        }
    }

    hasErrors () {
        return this.reportedErrors.size > 0;
    }

    getErrors<K extends keyof T & string> (fieldName: K) : string[] {
        return this.reportedErrors.get(fieldName) ?? [];
    }
}

export class FormController<T extends Dictionary> implements IObservable {

    private observableEventEmitter = new EventEmitter<IObservable.Events>();
    public get eventUpdated() {
        return this.observableEventEmitter.createEventReference('eventUpdated')
    }

    private _formData;
    private _formFieldsErrors: {
        [fieldName in keyof T]: string[] | undefined;
    };
    private _hasErrors?: boolean = undefined;
    private disabledFieldsMap;
    private disabledFieldsNames;
    private formSubmissionTriggered;
    private validationFunction;
    private disabledFieldsSpec;
    private sanitizationFunction;
    private fieldsForErrorReporting;
    private onValueChange;
    private initialValues;
    private formFieldsAutoFill;
    private modifiedFields = new Set<keyof T>();

    public readonly formFieldsSetters: {
        [fieldName in keyof T]: (value: T[fieldName]) => void
    };

    public get formData () { return this._formData; }
    public get formFieldsErrors () { return this._formFieldsErrors; }

    constructor(
        initialValues: T,
        options: FormController.Options<T>
    ) {
        this._formData = this.initialValues = initialValues;
        this.formSubmissionTriggered = false;
        this._formFieldsErrors = objectMapValues(initialValues, () => undefined) as any;
        this.fieldsForErrorReporting = new Set<keyof T>();
        this.validationFunction = options.validationFunction;
        this.disabledFieldsSpec = options.disabledFields;
        this.sanitizationFunction = options.sanitizationFunction ?? identity;
        this.formFieldsAutoFill = options.formFieldsAutoFill;
        this.onValueChange = options.onValueChange;
        this.formFieldsSetters = objectMapValues(initialValues, (_, fieldName) => {
            return (value: T[keyof T]) => {
                this.setValue(fieldName, value);
            }
        }) as any;

        const disabledFieldsMap = this.disabledFieldsMap = formControllerResolveInitialDisabledFieldsMap({
            initialValues: initialValues,
            disabledFields: options.disabledFields
        })

        this.disabledFieldsNames = formControllerResolveDisabledFieldsNames(disabledFieldsMap);

        EventEmitter.on(this.eventUpdated, () => {
            options.onUpdate?.(this);
        })
    }

    public isValid () {
        return this._hasErrors !== undefined && !this._hasErrors
    }

    private handleStateUpdate () {
        this.observableEventEmitter.emit('eventUpdated');
    }

    setValidationFunction (validationFunction: FormController.FormDataValidationFunction<T> | undefined) {
        if (validationFunction !== this.validationFunction) {
            this.validationFunction = validationFunction;
            this.validateForm();
        }
    }

    setDisabledFields (disabledFields: FormController.DisabledFieldsSpec<T> | undefined) {
        if (disabledFields !== this.disabledFieldsSpec) {
            this.disabledFieldsSpec = disabledFields;
            this.refreshDisabledFields();
        }
    }

    public refreshDisabledFields () {
        if (this.updateDisabledFields()) {
            this.observableEventEmitter.batchEventEmitting('eventUpdated', () => {
                this.validateForm();
                this.handleStateUpdate();
            })
        }
    }

    private updateDisabledFields () {
        const disabledFields = immerProduce(this.disabledFieldsMap, (disabledFields: typeof this.disabledFieldsMap) => {

            const updatedDisabledFields = evaluateWhenFunction(this.disabledFieldsSpec, this._formData);

            for (const key of objectGetKeys(disabledFields)) {
                disabledFields[key] = updatedDisabledFields?.[key] ?? false;
            }
        })

        if (disabledFields !== this.disabledFieldsMap) {
            this.disabledFieldsMap = disabledFields;
            this.disabledFieldsNames = formControllerResolveDisabledFieldsNames(disabledFields);

            return true;
        } else {
            return false;
        }
    }

    setSanitizationFunction (sanitizationFunction: FormController.FormDataSanitizationFunction<T> | undefined) {

        const definedSanitizationFunction = sanitizationFunction ?? identity;

        if (definedSanitizationFunction !== this.sanitizationFunction) {
            this.sanitizationFunction = definedSanitizationFunction;
            this.validateForm();
        }
    }

    public validateForm () {
        return this.validateFormInternal(this.sanitizationFunction(this._formData));
    }

    private validateFormInternal (formData: T) {
        const validationFunction = this.validationFunction;
        if (validationFunction) {

            const errorsReporter = new FormErrorsReporter<T>(formData, this.disabledFieldsMap);

            validationFunction(formData, errorsReporter);

            this.updateFieldsErrors(errorsReporter);

            return !errorsReporter.hasErrors();
        } else {
            return true;
        }
    }

    private updateFieldsErrors (errorsReporter: FormErrorsReporter<T>) {

        let shouldForceUpdate = false;
        for (const [key, prevErrors] of Object.entries<string[] | undefined>(this._formFieldsErrors)) {

            const fieldName = key as keyof T & string;

            if (this.formSubmissionTriggered || this.fieldsForErrorReporting.has(fieldName)) {
                const errorsArr = errorsReporter.getErrors(fieldName);

                if (!arrayEqual(prevErrors ?? [], errorsArr)) {
                    shouldForceUpdate = true;

                    this._formFieldsErrors[fieldName] = errorsArr.length === 0 ? undefined : errorsArr;
                }
            }
        }

        const prevHasErrors = this._hasErrors;
        const hasErrors = this._hasErrors = errorsReporter.hasErrors();


        if (shouldForceUpdate || prevHasErrors !== hasErrors) {
            this.handleStateUpdate();
        }
    }

    isFieldEnabled (fieldName: keyof T) : boolean {
        return !this.disabledFieldsMap[fieldName]
    }

    getDisabledFieldNames () {
        return this.disabledFieldsNames;
    }

    setFormData (formData: T | ((formData: T) => T)) {

        const prevFormData = this._formData;

        const updatedFormData = evaluateWhenFunction(formData, prevFormData);

        if (!objectShallowEqual(updatedFormData, prevFormData)) {
            this._formData = updatedFormData;

            this.updateDisabledFields();

            this.observableEventEmitter.batchEventEmitting('eventUpdated', () => {
                this.validateForm();
                this.handleStateUpdate();
            })
        }
    }

    startReportingErrors (fieldName: keyof T) {
        this.fieldsForErrorReporting.add(fieldName);
        this.validateForm();
    }

    getFormData () {
        return this._formData;
    }

    getValue<FIELD extends keyof T> (fieldName: FIELD) : T[FIELD] {
        return this._formData[fieldName];
    }

    setValue<FIELD extends keyof T> (fieldName: FIELD, value: T[FIELD]) {
        const formData = this._formData;
        const prevValue = formData[fieldName];
        if (prevValue !== value) {

            this.modifiedFields.add(fieldName);

            this._formData = {
                ...formData,
                [fieldName]: value
            }

            if (this.formFieldsAutoFill) {
                for (const [fieldName, autoFill] of Object.entries(this.formFieldsAutoFill)) {
                    if (!this.modifiedFields.has(fieldName)) {
                        this._formData[fieldName as keyof T] = autoFill(this._formData);
                    }
                }
            }

            this.updateDisabledFields();

            this.observableEventEmitter.batchEventEmitting('eventUpdated', () => {
                this.validateForm();
                this.handleStateUpdate();
            })

            this.onValueChange?.[fieldName]?.(value);
        }
    }

    resetFormState () {
        this.formSubmissionTriggered = false;
        this.fieldsForErrorReporting.clear();
        this._formFieldsErrors = objectMapValues(this._formData, () => undefined) as any;
        this._formData = this.initialValues;
        this.handleStateUpdate();
    }

    createSubmitEventHandler (
        submitCallback: (formData: T, errorsReporter: IFormErrorsReporter<T>) => Promisable<void>,
        options: {
            validationFailureCallback?: (formFieldsErrors: {
                [fieldName in keyof T]?: string[];
            }) => void;

            updateFormWithSanitizedData?: boolean;

            onBeforeValidation?: (formController: FormController<T>) => void;

            fieldsFocusOrderOnValidationError?: {
                fieldName: keyof T;
                focus: () => void;
            }[];
        } = {}
    ) {

        const {
            validationFailureCallback,
            onBeforeValidation,
            fieldsFocusOrderOnValidationError,
            updateFormWithSanitizedData = false
        } = options;

        return (event?: FormEvent) => {
            event?.preventDefault();

            this.formSubmissionTriggered = true;

            onBeforeValidation?.(this);

            const sanitizedFormData = this.sanitizationFunction(this._formData);

            const formIsValid = this.observableEventEmitter.batchEventEmitting('eventUpdated', () => {
                if (updateFormWithSanitizedData && !objectShallowEqual(sanitizedFormData, this._formData)) {
                    this._formData = sanitizedFormData;
                    this.handleStateUpdate();
                }

                return this.validateFormInternal(sanitizedFormData);
            });

            if (formIsValid) {

                const errorsReporter = new FormErrorsReporter<T>(sanitizedFormData, this.disabledFieldsMap);

                Promise.resolve(submitCallback(sanitizedFormData, errorsReporter))
                    .then(() => {
                        if (errorsReporter.hasErrors()) {
                            this.updateFieldsErrors(errorsReporter);
                        }
                    }, () => {

                    })

            } else {

                const formFieldsErrors = this._formFieldsErrors;

                if (fieldsFocusOrderOnValidationError) {
                    for (const fieldFocusOnErrorInfo of fieldsFocusOrderOnValidationError) {
                        if (formFieldsErrors[fieldFocusOnErrorInfo.fieldName]) {
                            fieldFocusOnErrorInfo.focus();
                            break;
                        }
                    }
                }

                validationFailureCallback?.(formFieldsErrors);
            }
        }
    }

    sanitizeFormData () {
        const sanitizedFormData = this.sanitizationFunction(this._formData);

        if (!objectShallowEqual(sanitizedFormData, this._formData)) {
            this._formData = sanitizedFormData;
            this.handleStateUpdate();
        }
    }

    public getSanitizedFormData () {
        return this.sanitizationFunction(this._formData);
    }

    public formUpdateScope<R> (updateFunction: () => R) {
        return this.observableEventEmitter.batchEventEmitting('eventUpdated', updateFunction)
    }
}

export function useFormController<T extends {}> (
    initialValues: T,
    options: FormController.Options<T> = {}
) : FormController<T> {

    const {
        onUpdate,
        ...restOptions
    } = options;

    const renderState = useRenderState({
        onUpdate
    })

    const forceUpdate = useForceUpdate();
    const formController = useInstanceValue(() => {
        return new FormController(initialValues, {
            ...restOptions,
            onUpdate: (formController) => {
                renderState.current.onUpdate?.(formController);
                forceUpdate();
            }
        })
    })

    formController.setValidationFunction(options.validationFunction);
    formController.setDisabledFields(options.disabledFields);
    formController.setSanitizationFunction(options.sanitizationFunction);

    return formController;
}

export function formControllerResolveInitialDisabledFieldsMap<T extends {}> (
    options: {
        initialValues: T;
        disabledFields?: FormController.DisabledFieldsSpec<T>;
    }
) : {
    [fieldName in keyof T]: boolean;
} {
    const {
        initialValues,
        disabledFields
    } = options;

    return {
        ...objectMapValues(initialValues, () => false),
        ...(evaluateWhenFunction(disabledFields, initialValues) ?? {})
    }
}

export function formControllerResolveDisabledFieldsNames<T extends {}> (
    disabledFieldsMap: {
        [fieldName in keyof T]: boolean;
    }
) {
    return Object.keys(objectFilterEntries(
        disabledFieldsMap,
        (disabled: boolean) => disabled
    )) as (keyof T)[]
}